import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { Flex } from '@atlaskit/primitives';

import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import { useAnalytics } from 'src/components/settings/analytics';
import { ConfirmationModal } from 'src/components/settings/modals';
import { useParams } from 'src/router/hooks';

import { useDequeuePullRequest } from '../hooks';
import { messages } from '../merge-queues.i18n';

export const PullRequestDequeueModal = ({
  onClose,
  focusedPullRequest,
  focusedMergeQueue,
}: {
  onClose: () => void;
  focusedPullRequest: number | undefined;
  focusedMergeQueue: MergeQueue | undefined;
}) => {
  const { publishButtonClickEvent } = useAnalytics({
    source: 'pullRequestRemoveFromQueueModal',
  });
  const { formatMessage } = useIntl();
  const { repositoryOwner: owner, repositorySlug: slug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();

  const { dequeuePullRequest, isDequeueingPullRequest } = useDequeuePullRequest(
    {
      mergeQueueUuid: focusedMergeQueue?.uuid || '',
      owner,
      slug,
      onClose,
    }
  );

  const modalBody = useMemo(
    () => (
      <Flex direction="column" gap="space.100">
        {formatMessage(messages.dequeuePullRequestModalBody)}
      </Flex>
    ),
    [formatMessage]
  );

  return (
    <ConfirmationModal
      appearance="warning"
      header={formatMessage(messages.dequeuePullRequestModalHeader, {
        pullRequestNumber: focusedPullRequest,
      })}
      body={modalBody}
      onConfirm={() => {
        publishButtonClickEvent('confirmRemoveFromQueue');
        if (focusedPullRequest) {
          dequeuePullRequest(focusedPullRequest);
        }
      }}
      onCancel={onClose}
      isDisabled={!focusedPullRequest}
      isLoading={isDequeueingPullRequest}
      customButtonText={{
        confirm: formatMessage(messages.removeFromQueueAction),
        cancel: formatMessage(messages.cancel),
      }}
    />
  );
};
